import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getVehicleById } from '../data/vehiclesData';
import { Users, Briefcase, Fuel, Settings, Check, Phone, MessageCircle, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const vehicle = getVehicleById(id);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const whatsappNumber = "18099640099";

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Vehículo no encontrado</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-yellow-700 text-white px-6 py-3 hover:bg-yellow-800 transition-all duration-300"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  const handleWhatsApp = () => {
    // Create a more descriptive message with vehicle ID for identification
    const vehicleRef = vehicle.id.replace(/-/g, ' ').toUpperCase();
    const message = `Hola, vengo de la página web de La Rent Car de la Isla y estoy interesado en este vehículo:\n\n🚗 *${vehicle.name} ${vehicle.year}*\n📋 Código: ${vehicleRef}\n💰 Precio: $${vehicle.price} USD/día\n\n¿Está disponible?`;
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:+${whatsappNumber}`;
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % vehicle.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + vehicle.images.length) % vehicle.images.length);
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Back Button */}
      <div className="bg-zinc-900 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 lg:px-20 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver al catálogo</span>
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-12 lg:px-20 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Images */}
          <div>
            {/* Main Image Gallery */}
            <div className="relative bg-white rounded-lg overflow-hidden mb-6">
              <img
                src={vehicle.images[currentImageIndex]}
                alt={`${vehicle.name} ${vehicle.year}`}
                className="w-full h-[500px] object-contain"
              />
              
              {/* Navigation Arrows */}
              {vehicle.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 hover:bg-black text-white flex items-center justify-center transition-colors duration-300"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 hover:bg-black text-white flex items-center justify-center transition-colors duration-300"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 text-sm">
                {currentImageIndex + 1} / {vehicle.images.length}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {vehicle.images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {vehicle.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`bg-white rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      currentImageIndex === index
                        ? 'border-yellow-700'
                        : 'border-white/20 hover:border-yellow-600/50'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${vehicle.name} vista ${index + 1}`}
                      className="w-full h-20 object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Information */}
          <div>
            {/* Category Badge */}
            <div className="inline-block bg-yellow-700/20 border border-yellow-600/30 px-4 py-2 mb-4">
              <span className="text-yellow-600 text-sm font-medium">{vehicle.categoryLabel}</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              {vehicle.name}
            </h1>
            <p className="text-white/60 text-xl mb-6">{vehicle.year}</p>

            {/* Price */}
            <div className="bg-zinc-900 border border-white/10 p-6 mb-8">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-5xl font-bold text-yellow-600">${vehicle.price}</span>
                <span className="text-white/70 text-2xl">USD / día</span>
              </div>
              <p className="text-white/60 text-sm">*Precio no incluye combustible</p>
            </div>

            {/* Short Description */}
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              {vehicle.longDescription}
            </p>

            {/* Key Specs */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-zinc-900 border border-white/10 p-4">
                <Users className="w-6 h-6 text-yellow-600 mb-2" />
                <div className="text-white font-semibold">{vehicle.passengers} Pasajeros</div>
              </div>
              <div className="bg-zinc-900 border border-white/10 p-4">
                <Briefcase className="w-6 h-6 text-yellow-600 mb-2" />
                <div className="text-white font-semibold">{vehicle.luggage} Maletas</div>
              </div>
              <div className="bg-zinc-900 border border-white/10 p-4">
                <Fuel className="w-6 h-6 text-yellow-600 mb-2" />
                <div className="text-white font-semibold">{vehicle.fuel}</div>
              </div>
              <div className="bg-zinc-900 border border-white/10 p-4">
                <Settings className="w-6 h-6 text-yellow-600 mb-2" />
                <div className="text-white font-semibold">{vehicle.transmission}</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4 mb-8">
              <button
                onClick={handleWhatsApp}
                className="w-full bg-yellow-700 text-white py-4 px-6 font-semibold text-lg hover:bg-yellow-800 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <MessageCircle className="w-6 h-6" />
                Reservar por WhatsApp
              </button>
              <button
                onClick={handleCall}
                className="w-full bg-white/10 text-white py-4 px-6 font-semibold text-lg border border-white/20 hover:bg-white hover:text-black transition-all duration-300 flex items-center justify-center gap-3"
              >
                <Phone className="w-6 h-6" />
                Llamar Ahora
              </button>
            </div>

            {/* Trust Badge */}
            <div className="bg-zinc-900 border border-white/10 p-4 text-center">
              <p className="text-white/80 text-sm">
                ✓ Respuesta en minutos • ✓ Entrega gratis en aeropuerto • ✓ Servicio 24/7
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Information Tabs */}
        <div className="mt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Features */}
            <div className="bg-zinc-900 border border-white/10 p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Características</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {vehicle.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-white/80">
                    <Check className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Specifications */}
            <div className="bg-zinc-900 border border-white/10 p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Especificaciones</h2>
              <div className="space-y-4">
                {Object.entries(vehicle.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center pb-4 border-b border-white/10 last:border-0">
                    <span className="text-white/70 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-white font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-zinc-900 border border-white/10 p-8 md:col-span-2">
              <h2 className="text-2xl font-bold text-white mb-6">Incluido en el Precio</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicle.included.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-white/80">
                    <Check className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-yellow-700 p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Listo para Reservar?
          </h3>
          <p className="text-white/90 text-lg mb-8">
            Reserva ahora y asegura tu {vehicle.name} {vehicle.year}
          </p>
          <button
            onClick={handleWhatsApp}
            className="inline-flex items-center justify-center gap-3 bg-black text-white px-10 py-5 font-semibold text-lg hover:bg-zinc-900 transition-all duration-300"
          >
            <MessageCircle className="w-6 h-6" />
            Reservar por WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
